import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  DailySummaryResponseSchema,
  DailySummarySettingsResponseSchema,
  type DailySummary,
} from "@oyako-flow/contracts";
import type { z } from "zod";
import { createOyakoFlowClient } from "../../../clients/typescript/src/index";

type SettingsResponse = z.infer<typeof DailySummarySettingsResponseSchema>;
const statusLabel: Record<string, string> = {
  pending: "送信待ち",
  sending: "送信中",
  accepted: "配信サービス受付済み",
  failed: "送信できませんでした",
  unknown: "送信結果を確認できません",
  cancelled: "送信を停止しました",
};
const errorCopy: Record<string, string> = {
  DAILY_SUMMARY_CONFLICT:
    "設定が更新されたか、確認メールを送った直後です。再読込し、再送は1分待ってからお試しください。",
  SUMMARY_CODE_INVALID: "確認コードが違います。メールの6桁を確認してください。",
  SUMMARY_CODE_EXPIRED: "確認コードの期限が切れました。もう一度メールを送ってください。",
  SUMMARY_EMAIL_NOT_READY: "先にメールアドレスを確認してください。",
  SUMMARY_EMAIL_UNAVAILABLE: "メール配信の準備が完了していません。",
  SUMMARY_CHANNEL_REQUIRED: "Web Pushかメールを選んでください。",
  AUTHENTICATION_REQUIRED: "ログインし直してください。",
  DAILY_SUMMARY_NOT_FOUND: "この日のサマリーはありません。",
};
function failure(error: unknown): Error {
  const code =
    typeof error === "object" &&
    error !== null &&
    "error" in error &&
    typeof error.error === "object" &&
    error.error !== null &&
    "code" in error.error
      ? String(error.error.code)
      : "";
  return new Error(
    errorCopy[code] ?? "通信できませんでした。接続を確認して、もう一度お試しください。",
  );
}
export function DailySummarySettings({
  workspaceId,
  pushControl,
}: {
  workspaceId: string;
  pushControl: ReactNode;
}) {
  const [data, setData] = useState<SettingsResponse | null>(null);
  const [draft, setDraft] = useState({ enabled: false, web_push: true, email: false });
  const [draftVersion, setDraftVersion] = useState(0);
  const [address, setAddress] = useState("");
  const [code, setCode] = useState("");
  const [summary, setSummary] = useState<DailySummary | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const client = useMemo(
    () =>
      createOyakoFlowClient("", async (input, init) => {
        const request = new Request(input, init);
        if (request.method !== "GET") {
          const cookie = document.cookie
            .split(";")
            .map((item) => item.trim())
            .find((item) => item.startsWith("oyako_csrf="));
          if (cookie)
            request.headers.set(
              "X-CSRF-Token",
              decodeURIComponent(cookie.slice("oyako_csrf=".length)),
            );
        }
        return fetch(request, { credentials: "same-origin" });
      }),
    [],
  );
  const path = useMemo(() => ({ workspaceId }), [workspaceId]);
  const load = useCallback(
    async (reset = true) => {
      const response = await client.GET("/api/v1/workspaces/{workspaceId}/daily-summary/settings", {
        params: { path },
      });
      if (response.error) throw failure(response.error);
      const next = DailySummarySettingsResponseSchema.parse(response.data);
      setData(next);
      if (reset) {
        setDraftVersion(next.settings.version);
        setDraft({
          enabled: next.settings.enabled,
          web_push: next.settings.web_push,
          email: next.settings.email,
        });
        setAddress(next.settings.email_address ?? "");
      }
    },
    [client, path],
  );
  const preview = useCallback(
    async (date?: string) => {
      const response = await client.GET("/api/v1/workspaces/{workspaceId}/daily-summary", {
        params: { path, query: date === undefined ? {} : { date } },
      });
      if (response.error) throw failure(response.error);
      setSummary(DailySummaryResponseSchema.parse(response.data).summary);
    },
    [client, path],
  );
  useEffect(() => {
    const timer = window.setTimeout(() => {
      void load().catch((cause: unknown) =>
        setError(cause instanceof Error ? cause.message : "受信設定を読み込めませんでした。"),
      );
      const search = new URLSearchParams(window.location.search);
      if (search.get("summary_workspace") === workspaceId)
        void preview(search.get("summary_date") ?? undefined).catch((cause: unknown) =>
          setError(cause instanceof Error ? cause.message : "サマリーを読み込めませんでした。"),
        );
    }, 0);
    return () => window.clearTimeout(timer);
  }, [load, preview, workspaceId]);
  useEffect(() => {
    const timer = window.setInterval(() => {
      void load(false).catch(() => undefined);
    }, 15_000);
    return () => window.clearInterval(timer);
  }, [load]);
  async function perform(action: () => Promise<void>, success: string, reload = true) {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      await action();
      if (reload) await load();
      setMessage(success);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "操作を完了できませんでした。");
    } finally {
      setBusy(false);
    }
  }
  const headers = () => ({ "Idempotency-Key": crypto.randomUUID() });
  return (
    <section
      className="panel settings-wide daily-summary-panel"
      aria-labelledby="daily-summary-title"
    >
      <div className="section-heading">
        <div>
          <p className="eyebrow">一日のふりかえり</p>
          <h2 id="daily-summary-title">毎日20:00のサマリー</h2>
        </div>
        <button
          disabled={busy}
          onClick={() => void perform(() => preview(), "いまの状況を表示しました。", false)}
        >
          いまの内容を見る
        </button>
      </div>
      <p>
        すべてのお子さんの「できたこと」「やり残し」「持ち越し」「たすけて」「確認待ち」をまとめて、あなたに送ります。予定は自動で変更しません。
      </p>
      {new URLSearchParams(window.location.search).has("summary_workspace") &&
      new URLSearchParams(window.location.search).get("summary_workspace") !== workspaceId ? (
        <p role="status">通知を受け取った家族に切り替えると、そのサマリーを確認できます。</p>
      ) : null}
      {error ? (
        <p role="alert" className="error-message">
          {error}
        </p>
      ) : null}
      {message ? <p role="status">{message}</p> : null}
      {data === null ? (
        <p>
          受信設定を読み込んでいます。
          <button onClick={() => void perform(() => load(), "再読込しました。")}>再読込</button>
        </p>
      ) : (
        <>
          <p>
            配信時刻: 毎日20:00（{data.settings.timezone}
            ）。このサマリーは、ほかの通知の「おやすみ時間」とは別に送ります。
          </p>
          {draftVersion !== data.settings.version ? (
            <p role="status">
              ほかの操作で受信設定が更新されています。
              <button
                disabled={busy}
                onClick={() => void perform(() => load(), "最新の設定を読み込みました。", false)}
              >
                設定を再読込
              </button>
            </p>
          ) : null}
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void perform(async () => {
                const result = await client.POST(
                  "/api/v1/workspaces/{workspaceId}/daily-summary/settings",
                  {
                    params: { path, header: headers() },
                    body: { ...draft, expected_version: draftVersion },
                  },
                );
                if (result.error) throw failure(result.error);
              }, "受信設定を保存しました。");
            }}
          >
            <fieldset disabled={busy}>
              <legend>あなたの受信設定</legend>
              <label>
                <input
                  type="checkbox"
                  checked={draft.enabled}
                  onChange={(event) => setDraft({ ...draft, enabled: event.target.checked })}
                />
                毎日のサマリーを受け取る
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={draft.web_push}
                  onChange={(event) => setDraft({ ...draft, web_push: event.target.checked })}
                />
                Web Pushで受け取る
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={draft.email}
                  disabled={!data.settings.email_verified || !data.settings.email_available}
                  onChange={(event) => setDraft({ ...draft, email: event.target.checked })}
                />
                メールで受け取る
              </label>
              <p>
                {data.settings.email_address
                  ? `確認済みの宛先: ${data.settings.email_address}`
                  : "メールアドレスは未登録です。"}
              </p>
              <button type="submit" className="primary">
                受信設定を保存
              </button>
            </fieldset>
          </form>
          <div className="daily-summary-channel">
            <h3>Web Pushの端末設定</h3>
            {pushControl}
            <p>
              通知を許可した端末: {data.settings.push_devices}
              台。iPhone・iPadではホーム画面に追加したアプリから通知を許可してください。
            </p>
          </div>
          <div className="daily-summary-channel">
            <h3>サマリーメールの宛先</h3>
            <p>
              メールには、お子さんの名前と予定のタイトルが含まれます。写真や記録本文は含めません。
            </p>
            {!data.settings.email_available ? (
              <p role="status">メール配信の準備中です。Web Pushは利用できます。</p>
            ) : (
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  void perform(async () => {
                    const result = await client.POST(
                      "/api/v1/workspaces/{workspaceId}/daily-summary/email/request",
                      {
                        params: { path, header: headers() },
                        body: { expected_version: data.settings.version, email: address },
                      },
                    );
                    if (result.error) throw failure(result.error);
                  }, "確認メールの送信を受け付けました。届いた6桁のコードを入力してください。");
                }}
              >
                <fieldset disabled={busy}>
                  <legend>メールアドレスを確認する</legend>
                  <label>
                    メールアドレス
                    <input
                      type="email"
                      autoComplete="email"
                      required
                      maxLength={254}
                      value={address}
                      onChange={(event) => setAddress(event.target.value)}
                    />
                  </label>
                  <button type="submit">確認メールを送る</button>
                </fieldset>
              </form>
            )}
            {data.verification ? (
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  const verification = data.verification;
                  if (!verification) return;
                  void perform(async () => {
                    const result = await client.POST(
                      "/api/v1/workspaces/{workspaceId}/daily-summary/email/confirm",
                      {
                        params: { path, header: headers() },
                        body: { challenge_id: verification.id, code },
                      },
                    );
                    if (result.error) throw failure(result.error);
                    setCode("");
                  }, "メールアドレスを確認しました。「メールで受け取る」を選び、受信設定を保存してください。");
                }}
              >
                <fieldset disabled={busy}>
                  <legend>{data.verification.email} の確認</legend>
                  <p>{statusLabel[data.verification.status]}。有効期限は30分です。</p>
                  <label>
                    6桁の確認コード
                    <input
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      pattern="[0-9]{6}"
                      maxLength={6}
                      required
                      value={code}
                      onChange={(event) => setCode(event.target.value)}
                    />
                  </label>
                  <button type="submit">メールアドレスを確認</button>
                </fieldset>
              </form>
            ) : null}
          </div>
          <details>
            <summary>最近の配信状況</summary>
            <p>
              「配信サービス受付済み」は、端末への表示・メールの開封を確認した意味ではありません。
            </p>
            {data.deliveries.length === 0 ? (
              <p>配信履歴はまだありません。</p>
            ) : (
              <ul>
                {data.deliveries.map((item) => (
                  <li key={item.id}>
                    {item.local_date} / {item.channel === "email" ? "メール" : "Web Push"} /{" "}
                    {statusLabel[item.status]}
                  </li>
                ))}
              </ul>
            )}
          </details>
        </>
      )}
      {summary ? (
        <div className="daily-summary-preview">
          <h3>{summary.local_date} のサマリー</h3>
          <p>
            {new Intl.DateTimeFormat("ja-JP", {
              timeZone: summary.timezone,
              dateStyle: "medium",
              timeStyle: "short",
            }).format(new Date(summary.generated_at))}
            時点
          </p>
          {summary.children.length === 0 ? (
            <p>登録中のお子さんはいません。</p>
          ) : (
            summary.children.map((child) => (
              <article key={child.id}>
                <h4>{child.name}</h4>
                {(
                  [
                    ["今日できたこと", child.completed],
                    ["今日のやり残し", child.remaining],
                    ["前日までの持ち越し", child.carryover],
                    ["たすけて", child.help],
                    ["保護者の確認待ち", child.awaiting_review],
                  ] as const
                ).map(([label, group]) => (
                  <div key={label}>
                    <strong>
                      {label}: {group.count}件
                    </strong>
                    {group.tasks.length > 0 ? (
                      <ul>
                        {group.tasks.map((task) => (
                          <li key={task.id}>{task.title}</li>
                        ))}
                      </ul>
                    ) : null}
                    {group.count > group.tasks.length ? (
                      <p>ほか{group.count - group.tasks.length}件</p>
                    ) : null}
                  </div>
                ))}
                <p>
                  延期 {child.deferred}件 / スキップ {child.skipped}件 / 取消 {child.cancelled}件
                </p>
              </article>
            ))
          )}
        </div>
      ) : null}
    </section>
  );
}
